import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import workspaceServices, {
  type IWorkspaceMember,
} from "#src/services/workspace";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { ButtonV2 } from "#src/commonV2/Button";
import { updateCurrentWorkspaceV2 } from "#src/store/slices/auth";
import store from "#src/store";
import { ModalHeaderIcon } from "#src/commonV2/modal/ModalHeaderIcon";
import { InfoCircle } from "@untitled-ui/icons-react";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";

type ITransferOwnerModalProps = {
  open: boolean;
  onClose: () => void;
  selectedMember: IWorkspaceMember | null;
  workspaceId: string;
  updateMemberList: () => void;
};

export const TransferOwnerModal = ({
  open,
  onClose,
  workspaceId,
  selectedMember,
  updateMemberList,
}: ITransferOwnerModalProps) => {
  const { t } = useTranslation("workspace", {
    keyPrefix: "member.transferMemberModal",
  });

  // --------------- DATA ---------------

  const [isLoading, setIsLoading] = useState(false);

  // --------------- METHODS ---------------

  const transferOwnership = async () => {
    if (workspaceId && selectedMember) {
      setIsLoading(true);
      workspaceServices
        .transfer_ownership(workspaceId, selectedMember.id)
        .then(async (response) => {
          if (response.success) {
            updateMemberList();
            await workspaceServices
              .retrieve_workspace(workspaceId)
              .then((response) => {
                store.dispatch(updateCurrentWorkspaceV2(response));
              })
              .catch(apiErrorHandler);
            onClose();
          }
        })
        .catch((error) => {
          apiErrorHandler(error, {
            toastMessage: t("notification.transfer.fail"),
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  // --------------- RENDER ---------------

  return (
    <ModalV2 open={open} onChangeOpen={onClose} className="w-[40rem] gap-8">
      <div className="flex gap-4">
        <ModalHeaderIcon type="warning" Icon={InfoCircle} />
        <div className="flex flex-col gap-4 text-md text-gray-v2-600">
          <span className="text-lg font-semibold text-gray-v2-900">
            {t("title")}
          </span>
          {/* Desc */}
          <Trans
            t={t}
            i18nKey={"desc"}
            values={{
              email: selectedMember?.user?.email || selectedMember?.email,
            }}
            components={{
              span: (
                <span className="contents text-md font-semibold text-gray-v2-900" />
              ),
            }}
          />
          <ul className="list-disc pl-8">
            <li>{t("desc1")}</li>
            <li>{t("desc2")}</li>
            <li>{t("desc3")}</li>
            <li className="font-semibold text-gray-v2-900">{t("desc4")}</li>
          </ul>
          <Trans
            t={t}
            i18nKey={"note"}
            components={{
              span: (
                <span className="contents text-md font-semibold text-brand-v2-600" />
              ),
            }}
          />
          {/* Desc end */}
        </div>
      </div>
      {/* Footer */}
      <div className="flex gap-3 justify-end">
        <ButtonV2 variant="secondary" size="lg" color="gray" onClick={onClose}>
          <p>{t("button.cancel")}</p>
        </ButtonV2>

        <ButtonV2
          variant="primary"
          size="lg"
          pending={isLoading}
          onClick={transferOwnership}
        >
          <p>{t("button.transfer")}</p>
        </ButtonV2>
      </div>
      {/* Footer end */}
    </ModalV2>
  );
};
